import { FiRefreshCw } from "react-icons/fi";

interface ConsensusRowState {
  consensusScore: number | null;
  panelNotes: string | null;
  rfxInviteId: number;
}

interface ConsensusRowProps {
  average: number | null;
  state: ConsensusRowState;
  supplierName: string;
  onChange: (updated: ConsensusRowState) => void;
  onActiveScoreChange?: (score: number | null) => void;
}

export const ConsensusRow: React.FC<ConsensusRowProps> = ({
  supplierName,
  state,
  average,
  onChange,
  onActiveScoreChange,
}) => {
  const handleScoreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const parsed = raw === "" ? null : parseFloat(raw);
    const newScore = isNaN(parsed as number) ? null : parsed;
    onChange({ ...state, consensusScore: newScore });
    onActiveScoreChange?.(newScore);
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange({ ...state, panelNotes: e.target.value || null });
  };

  const handleScoreFocus = () => {
    onActiveScoreChange?.(state.consensusScore);
  };

  const handleScoreBlur = () => {
    onActiveScoreChange?.(null);
  };

  const handleUseAverage = () => {
    if (average !== null) {
      onChange({ ...state, consensusScore: average });
      onActiveScoreChange?.(average);
    }
  };

  return (
    <div className="flex items-start gap-4 py-3 border-b border-gray-100 last:border-b-0">
      <div className="w-40 flex-shrink-0 pt-1">
        <span className="text-sm font-medium text-gray-900 line-clamp-2">{supplierName}</span>
        {average !== null && (
          <span className="block text-xs text-gray-500 mt-0.5">Avg: {average.toFixed(2)}</span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="number"
          min={0}
          max={10}
          step={1}
          value={state.consensusScore ?? ""}
          onChange={handleScoreChange}
          onFocus={handleScoreFocus}
          onBlur={handleScoreBlur}
          placeholder="—"
          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          aria-label={`Consensus score for ${supplierName}`}
        />
        {average !== null && (
          <button
            type="button"
            onClick={handleUseAverage}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
            aria-label={`Use average score for ${supplierName}`}
          >
            <FiRefreshCw className="w-3 h-3" />
            Use Avg
          </button>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <textarea
          value={state.panelNotes ?? ""}
          onChange={handleNotesChange}
          placeholder="Panel notes (optional)"
          rows={2}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          aria-label={`Panel notes for ${supplierName}`}
        />
      </div>
    </div>
  );
};
